/**
 * `/queue?tab=history` — TASK 9.1 Step 4 (AC 7.3 / AC 7.6, mock-defect fix 9).
 *
 * The mock's history sub-tab (L613-627): the last 100 finished/skipped tracks,
 * newest first, each row a status pill (FINISHED teal / SKIPPED yellow, ink
 * text — ui-contract §3 /queue history block) + title/`artist — album` + a
 * "↺ Re-add" button, wired to `GET /api/queue/history` (TASK 6.4) instead of
 * the in-memory `db.history`.
 *
 * MOCK-DEFECT FIX 9: the mock could only ever REACH its empty state because
 * `db.history` seeds empty — it had no loading and no error rendering at all,
 * and a failed fetch would have left the panel blank. Here the tab has the
 * full state family: skeleton while in flight, the §10.6-pinned empty copy
 * ("Nothing played yet — history fills up as tracks finish or get skipped."),
 * and a retriable ErrorBlock. Never a blank panel (AC 7.6).
 *
 * Re-add calls `POST /api/queue/history/:id/readd` — the track id comes from
 * the ROW server-side, never from this client. Its failure family is the
 * queue-add family: the 1,000-item cap is a 422 whose ACTIONABLE text lives in
 * `fieldErrors` (`error.message` is the generic "Some fields are invalid."),
 * a vanished track is a 410 GONE, and an entry pruned on another device is a
 * 404 — which means THIS list is stale, so the tab refreshes it.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 9.1's files */

import { ERROR_CODES, TESTIDS, ordinalTestId } from '@streamymusic/shared';

import { ApiError, fieldErrorText } from '../../../api/api-client.js';
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  RETRIABLE_ERROR_MESSAGE,
} from '../../components/StateBlocks.js';
import { append, el } from '../../components/primitives/dom.js';
import { Pill } from '../../components/primitives/Pill.js';

/** AC 7.3 — the server retains 100 rows, so one page of 100 IS the tab. */
export const HISTORY_PAGE_SIZE = 100;

/** mock L612 — the intro line above the rows. */
export const HISTORY_INTRO = 'Last 100 finished or skipped tracks — most recent first.';

/** The §10.6-pinned empty copy (mock L615) — mock-defect fix 9's whole point. */
export const HISTORY_EMPTY_MESSAGE =
  'Nothing played yet — history fills up as tracks finish or get skipped.';
/** Headline over the empty copy — StateBlocks' glyph line (the mock had none). */
export const HISTORY_EMPTY_HEADLINE = 'NOTHING PLAYED YET';

/** Announced when a re-add lands (mock `_addQ`'s success toast). */
export const HISTORY_READD_SUCCESS_MESSAGE = 'Added to queue';

/**
 * Builds the history tab body and starts loading it. QueueView mounts this
 * under its own heading + tab strip; it is a body, not a route.
 *
 * @param {{
 *   api: Pick<
 *     import('../../../api/api-client.js').ApiClient,
 *     'getQueueHistory'|'readdQueueHistory'
 *   >,
 *   limit?: number,
 *   announce?: (text: string) => void,
 * }} options
 *   `announce` is the shell's persistent polite live region (LiveRegions.js)
 *   — see StateBlocks.js#ErrorBlock for why freshly inserted text cannot
 *   announce itself.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function HistoryTab(options) {
  const { api, limit = HISTORY_PAGE_SIZE, announce } = options;

  const container = el('div');
  const intro = el('p', { className: 'sm-count-label', text: HISTORY_INTRO });
  const body = el('div');
  /** Re-add failures land here so the rows they refer to stay on screen. */
  const notice = el('div');
  append(container, intro, body, notice);

  let generation = 0;
  let disposed = false;
  /** @type {AbortController|null} */
  let controller = null;

  /**
   * @param {import('@streamymusic/shared').QueueHistoryEntry} entry
   * @param {number} ordinal 1-based row ordinal (the contract's
   *   ordinal-duplicate convention: first row bare, then `-2`, `-3`, …)
   * @returns {HTMLElement}
   */
  function HistoryRow(entry, ordinal) {
    const finished = entry.reason === 'finished';
    const row = el('div', {
      className: 'sm-row sm-row--history',
      testId: ordinalTestId(TESTIDS.HISTORY_ROW, ordinal),
    });

    const readd = el('button', {
      className: 'sm-btn sm-btn--xs sm-btn--xs-panel',
      text: '↺ Re-add',
      testId: ordinalTestId(TESTIDS.HISTORY_READD_BUTTON, ordinal),
      attrs: { type: 'button', 'aria-label': `Re-add ${entry.track.title}` },
      on: {
        click: () => {
          void readdEntry(entry, readd);
        },
      },
    });

    append(
      row,
      // mock L619 — FINISHED teal / SKIPPED yellow, ink text on both, so the
      // reason is carried by the LABEL (§8.5), the fill is only reinforcement.
      Pill({
        label: finished ? 'FINISHED' : 'SKIPPED',
        ...(finished ? { background: 'var(--sm-color-teal)' } : {}),
      }),
      append(
        el('span', { className: 'sm-flex-fill' }),
        el('span', { className: 'sm-line-primary', text: entry.track.title }),
        el('span', {
          className: 'sm-line-secondary',
          text: `${entry.track.artistName} — ${entry.track.albumTitle}`,
        }),
      ),
      readd,
    );
    return row;
  }

  /**
   * @param {import('@streamymusic/shared').QueueHistoryEntry} entry
   * @param {HTMLElement} button
   * @returns {Promise<void>}
   */
  async function readdEntry(entry, button) {
    button.setAttribute('disabled', '');
    notice.replaceChildren();
    try {
      await api.readdQueueHistory(entry.id);
      if (disposed) return;
      if (announce !== undefined) announce(HISTORY_READD_SUCCESS_MESSAGE);
    } catch (error) {
      if (disposed) return;
      // The queue cap arrives as a 422 whose real text is keyed in
      // `fieldErrors` — `error.message` is the generic envelope copy.
      const text =
        fieldErrorText(error) ??
        (error instanceof ApiError ? error.message : RETRIABLE_ERROR_MESSAGE);
      notice.replaceChildren(
        el('p', { className: 'sm-form-error', text, attrs: { role: 'alert' } }),
      );
      if (announce !== undefined) announce(text);
      if (/** @type {{ code?: string }} */ (error).code === ERROR_CODES.NOT_FOUND) {
        // The entry is gone (pruned / another device): this list is stale.
        void load();
      }
    } finally {
      button.removeAttribute('disabled');
    }
  }

  async function load() {
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    body.replaceChildren(LoadingBlock({ count: 6, label: 'Loading history' }));

    try {
      const page = await api.getQueueHistory({ limit, signal: ownController.signal });
      if (disposed || own !== generation) return;

      if (page.items.length === 0) {
        // Mock-defect fix 9 / AC 7.6: the empty state RENDERS — never blank.
        body.replaceChildren(
          EmptyBlock({
            headline: HISTORY_EMPTY_HEADLINE,
            message: HISTORY_EMPTY_MESSAGE,
            ...(announce === undefined ? {} : { announce }),
          }),
        );
        return;
      }

      const rows = el('div', { className: 'sm-stack-6' });
      page.items.forEach((entry, index) => {
        rows.append(HistoryRow(entry, index + 1));
      });
      body.replaceChildren(rows);
    } catch {
      if (disposed || own !== generation) return;
      body.replaceChildren(
        ErrorBlock({
          onRetry: () => {
            void load();
          },
          ...(announce === undefined ? {} : { announce }),
        }),
      );
    }
  }

  void load();

  return {
    element: container,
    dispose() {
      disposed = true;
      controller?.abort();
    },
  };
}
